/**
 * Fill-once step skipping for the three onboarding modals.
 *
 * Each modal keeps its own hand-rolled `page` state ("1".."7"). Rather than
 * rewrite those flows, this helper takes the modal's step descriptor and the
 * known profile and answers three questions:
 *
 *   visibleSteps()  which pages still need to be shown
 *   firstStep()     where to start
 *   nextStep(page)  where to go after `page`, skipping known ones
 *
 * A step is skippable only when the student is signed in AND every durable
 * field it collects is already known. Per-attempt steps (test module, test
 * language) declare no fields and are therefore never skipped.
 */
import { isKnown } from "../Services/studentAuth";

/**
 * @typedef {Object} StepDescriptor
 * @property {string} page  the modal's own page id
 * @property {string[]} [fields]  durable profile fields this step collects
 * @property {boolean} [alwaysAsk]  test-specific question: shown every
 *   attempt (preselected with the saved answer) instead of being skipped
 */

/**
 * @param {StepDescriptor[]} steps - in flow order
 * @param {Object} known - getKnownProfile() result
 * @returns {StepDescriptor[]} the steps that still need answering
 */
export const visibleSteps = (steps, known) =>
  steps.filter((step) => {
    // test-specific steps are shown on EVERY attempt, preselected with the
    // saved answer, so the student can confirm or change it each time
    if (step.alwaysAsk) return true;
    const fields = step.fields || [];
    if (!fields.length) return true; // per-attempt step — always ask
    return !fields.every((field) => isKnown(known, field));
  });

/**
 * @returns {string|null} first page to show, or null when nothing is left
 */
export const firstStep = (steps, known) => {
  const visible = visibleSteps(steps, known);
  return visible.length ? visible[0].page : null;
};

/**
 * @returns {string|null} next page after `page`, or null when the flow is done
 */
export const nextStep = (steps, known, page) => {
  const visible = visibleSteps(steps, known);
  const index = visible.findIndex((step) => step.page === page);
  if (index === -1) {
    // `page` was itself skipped — fall back to the next visible step in the
    // full ordering so a manual jump can never strand the student
    const order = steps.findIndex((step) => step.page === page);
    const after = visible.find(
      (step) => steps.findIndex((s) => s.page === step.page) > order,
    );
    return after ? after.page : null;
  }
  return index + 1 < visible.length ? visible[index + 1].page : null;
};

/** @returns {string|null} previous visible page, for Back buttons */
export const prevStep = (steps, known, page) => {
  const visible = visibleSteps(steps, known);
  const index = visible.findIndex((step) => step.page === page);
  return index > 0 ? visible[index - 1].page : null;
};

/**
 * Progress across the VISIBLE steps only, so a shortened flow still fills the
 * bar to 100%.
 * @returns {number} 4..100
 */
export const stepProgress = (steps, known, page) => {
  const visible = visibleSteps(steps, known);
  if (visible.length <= 1) return 100;
  const index = Math.max(0, visible.findIndex((step) => step.page === page));
  return Math.max(4, Math.round((index / (visible.length - 1)) * 100));
};

/**
 * The durable fields this flow skipped — what the "using your saved details"
 * summary lists.
 * @returns {string[]}
 */
export const skippedFields = (steps, known) => {
  const visiblePages = new Set(visibleSteps(steps, known).map((s) => s.page));
  return steps
    .filter((step) => !visiblePages.has(step.page))
    .flatMap((step) => step.fields || []);
};
